import { lazy, Suspense, useMemo, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, LazyMotion, domAnimation } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageMeta from '@/components/seo/PageMeta';
import SportEmoji from '@/components/SportEmojis';
import { ArrowRight, Shield, Filter, Clock, Smile, CirclePlay, CheckCircle, Users } from 'lucide-react';
import { sportDescriptions } from '@/utils/sportUtils';
import { fetchGamesData } from '@/api/gamesService';
import { SportType } from '@/types';
import {
  HomeSportDestination,
  resolveHomeSportDestination,
} from '@/lib/homeSportDestination';

const SoccerPenaltyChallenge = lazy(() => import('@/components/SoccerPenaltyChallenge'));

const PenaltyRunPlaceholder = () => (
  <div
    aria-label="Loading Penalty Run"
    className="flex aspect-[10/14.6] items-center justify-center rounded-[2rem] bg-[#050c16] text-sm font-semibold text-slate-300 sm:aspect-[16/10.8] xl:aspect-[16/9.2]"
  >
    Setting up the penalty spot…
  </div>
);

const DeferredPenaltyRun = () => {
  const boundaryRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const boundary = boundaryRef.current;
    if (!boundary || shouldLoad) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '280px 0px' },
    );
    observer.observe(boundary);
    return () => observer.disconnect();
  }, [shouldLoad]);

  return (
    <div ref={boundaryRef}>
      {shouldLoad ? (
        <Suspense fallback={<PenaltyRunPlaceholder />}>
          <SoccerPenaltyChallenge />
        </Suspense>
      ) : (
        <PenaltyRunPlaceholder />
      )}
    </div>
  );
};

type GameSelectionLinkProps = {
  destination: HomeSportDestination;
  className: string;
  children: React.ReactNode;
  testId: string;
};

const GameSelectionLink = ({
  destination,
  className,
  children,
  testId,
}: GameSelectionLinkProps) => {
  if (destination.kind === 'fallback') {
    return (
      <a href={destination.path} className={className} data-testid={testId}>
        {children}
      </a>
    );
  }

  return (
    <Link
      to={destination.path}
      state={{ entrySource: 'home' }}
      className={className}
      data-testid={testId}
    >
      {children}
    </Link>
  );
};

const formatFeedFreshness = (lastUpdated?: string): string => {
  if (!lastUpdated) {
    return 'Live';
  }

  const diffInMinutes = Math.max(
    1,
    Math.round((Date.now() - new Date(lastUpdated).getTime()) / (1000 * 60))
  );

  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  if (diffInMinutes < 24 * 60) {
    return `${Math.round(diffInMinutes / 60)}h ago`;
  }

  return `${Math.round(diffInMinutes / (24 * 60))}d ago`;
};

const Home = () => {
  // Fetch games data to check availability for each sport
  const { data: gamesData } = useQuery({
    queryKey: ['games'],
    queryFn: fetchGamesData,
  });

  // Add preconnect for faster resource loading
  useEffect(() => {
    // Add preconnect hint for potential resources
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = window.location.origin;
    document.head.appendChild(preconnect);
    
    return () => {
      document.head.removeChild(preconnect);
    };
  }, []);

  // Sport cards data
  const sportCards = [
    { 
      name: 'NBA', 
      path: '/sport/nba',
      icon: <SportEmoji sportType="nba" size="large" className="text-white" />,
      description: sportDescriptions['nba'],
      accent: 'bg-orange-400',
      iconTone: 'border-orange-300/20 bg-orange-400/10',
      hoverTone: 'hover:border-orange-300/45 focus-visible:ring-orange-300/40'
    },
    { 
      name: 'Soccer', 
      path: '/sport/soccer',
      icon: <SportEmoji sportType="soccer" size="large" className="text-white" />,
      description: sportDescriptions['soccer'],
      accent: 'bg-emerald-400',
      iconTone: 'border-emerald-300/20 bg-emerald-400/10',
      hoverTone: 'hover:border-emerald-300/45 focus-visible:ring-emerald-300/40'
    },
    { 
      name: 'NFL', 
      path: '/sport/nfl',
      icon: <SportEmoji sportType="nfl" size="large" className="text-white" />,
      description: sportDescriptions['nfl'],
      accent: 'bg-sky-400',
      iconTone: 'border-sky-300/20 bg-sky-400/10',
      hoverTone: 'hover:border-sky-300/45 focus-visible:ring-sky-300/40'
    },
    { 
      name: 'MLB', 
      path: '/sport/mlb',
      icon: <SportEmoji sportType="mlb" size="large" className="text-white" />,
      description: sportDescriptions['mlb'],
      accent: 'bg-rose-400',
      iconTone: 'border-rose-300/20 bg-rose-400/10',
      hoverTone: 'hover:border-rose-300/45 focus-visible:ring-rose-300/40'
    },
    { 
      name: 'NHL', 
      path: '/sport/nhl',
      icon: <SportEmoji sportType="nhl" size="large" className="text-white" />,
      description: sportDescriptions['nhl'],
      accent: 'bg-cyan-400',
      iconTone: 'border-cyan-300/20 bg-cyan-400/10',
      hoverTone: 'hover:border-cyan-300/45 focus-visible:ring-cyan-300/40'
    },
  ];

  const getSportGameCount = (sportType: SportType): number => {
    if (!gamesData?.games) {
      return 0;
    }

    const sportGamesObj = gamesData.games[sportType];
    return sportGamesObj ? Object.keys(sportGamesObj).length : 0;
  };

  const liveSportNames = sportCards
    .filter(card => getSportGameCount(card.name.toLowerCase() as SportType) > 0)
    .map(card => card.name);

  const inactiveSportNames = sportCards
    .filter(card => getSportGameCount(card.name.toLowerCase() as SportType) === 0)
    .map(card => card.name);

  const gameSelectionDestination = resolveHomeSportDestination(gamesData?.games);
  
  // Benefits data with expanded descriptions
  const benefits = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Choose Before Spoilers",
      description: "Make the replay decision before standings, thumbnails, notifications, or score-first sports apps tell you what happened."
    },
    {
      icon: <Filter className="w-8 h-8" />,
      title: "Deterministic Watch Signals",
      description: "Use point margins, goals, runs, and competition filters to narrow the list without resorting to score reveals."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Spend Time On The Right Replay",
      description: "Shortlist the completed games most likely worth your time, then continue to the replay platform you already use."
    }
  ];

  // FAQ Data for display
  const faqItems = [
    {
      question: "How does skore keep games spoiler-free?",
      answer: "skore filters games based on excitement metrics like point difference or total goals without revealing actual scores. You can enjoy watching games based on how close or exciting they were without knowing the outcome beforehand."
    },
    {
      question: "What sports are available on skore?",
      answer: "skore has NBA, soccer, NFL, MLB, and NHL pages. The current public feed regularly shows NBA, soccer, MLB, and NHL games. NFL availability depends on the current data feed, so the NFL page may sometimes have no games."
    },
    {
      question: "Is skore free to use?",
      answer: "Yes, skore is completely free to use. There are no subscriptions or hidden fees."
    },
    {
      question: "How are games filtered?",
      answer: "Each sport uses a spoiler-safe signal: NBA, NFL, and MLB use margin, while soccer and NHL use total goals. You can adjust these filters to find games that match your preferences."
    }
  ];

  // Product facts - memoized
  const productFacts = useMemo(() => {
    const coverageFact = gamesData?.games
      ? { value: String(liveSportNames.length), label: 'Sports live now' }
      : { value: 'Live', label: 'Feed-aware coverage' };

    const freshnessFact = gamesData?.lastUpdated
      ? { value: formatFeedFreshness(gamesData.lastUpdated), label: 'Feed updated' }
      : { value: 'Safe', label: 'Choose first' };

    return [
      { value: 'Hidden', label: 'Scores by default' },
      coverageFact,
      { value: 'Free', label: 'No account' },
      freshnessFact,
    ];
  }, [gamesData?.games, gamesData?.lastUpdated, liveSportNames.length]);

  const liveCoverageSummary = !gamesData?.games
    ? 'Coverage updates with the current feed for each sport. Choose on skore first, then watch on your own replay platform.'
    : `${liveSportNames.length > 0
      ? `Live now: ${liveSportNames.join(', ')}.`
      : 'No sports are live in the current completed-game feed right now.'}${inactiveSportNames.length > 0
      ? ` ${inactiveSportNames.join(', ')} appear when new completed games reach the feed.`
      : ''}`;

  // Animation variants - optimized for performance
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.03,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 5 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "tween",
        duration: 0.2
      }
    }
  };

  // How it works animation states
  const processData = [
    { 
      number: "01", 
      title: "Open skore first",
      description: "Start here before score-first apps, standings pages, or social feeds tell you what happened.",
      icon: <div className="bg-black/30 rounded-full p-1.5"><SportEmoji sportType="nba" size="large" /></div>,
      color: "bg-orange-500"
    },
    { 
      number: "02", 
      title: "Narrow the replay list",
      description: "Use spoiler-safe filters to choose the kind of completed game you want without revealing the result.",
      icon: <Filter className="w-6 h-6" />,
      color: "bg-indigo-500"
    },
    { 
      number: "03", 
      title: "Watch Elsewhere",
      description: "Take your pick to the broadcaster, streaming app, DVR, or replay platform you already trust.",
      icon: <Smile className="w-6 h-6" />,
      color: "bg-green-500"
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-gray-950 to-gray-900">
      <PageMeta
        title="Watch Sports Without Spoilers | skore.info"
        description="Choose completed games worth watching before score-first apps spoil the result. skore keeps scores hidden, uses spoiler-safe filters, and points you to your own replay platform."
        canonicalPath="/"
        keywords={['sports', 'spoiler-free', 'NBA', 'MLB', 'NHL', 'soccer', 'sports replays']}
      />
      {/* Preload hero section background gradient */}
      <div className="hidden bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-900/20 to-transparent"></div>
      
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section - Optimized LCP */}
        <section className="relative overflow-hidden py-16 sm:py-20 md:py-28">
          <div
            aria-hidden="true"
            data-testid="home-hero-backdrop"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-900/20 to-transparent"
          ></div>
          
          <div className="container mx-auto px-4 sm:px-6">
            <div className="mx-auto max-w-5xl text-center">
              {/* Main heading - LCP element with optimized rendering */}
              <h1 className="mx-auto mb-6 max-w-[11ch] text-[clamp(2.8rem,8vw,5.75rem)] font-bold leading-[0.92] tracking-[-0.05em] text-white [text-wrap:balance]">
                <span className="block text-indigo-400">Choose The Replay,</span>
                <span className="block">Before The Score Finds You.</span>
              </h1>
              
              {/* Static paragraph for faster initial render */}
              <p className="mx-auto mb-8 max-w-2xl text-base leading-7 text-gray-300 [text-wrap:pretty] sm:text-lg md:text-xl">
                skore helps you shortlist completed games with scores hidden by default,
                then continue to your own broadcaster, streaming app, DVR, or replay platform.
              </p>

              <div className="mx-auto mb-8 flex max-w-xl flex-col items-center">
                <GameSelectionLink
                  destination={gameSelectionDestination}
                  testId="home-hero-game-selection"
                  className="group inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-indigo-950/35 transition hover:-translate-y-0.5 hover:from-indigo-400 hover:to-violet-400 hover:shadow-indigo-900/45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo-300 sm:w-auto sm:px-8"
                >
                  <span>{gameSelectionDestination.label}</span>
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </GameSelectionLink>
                <p className="mt-3 text-sm leading-5 text-slate-400">
                  {gameSelectionDestination.supportingText}
                </p>
              </div>

              {/* Product facts section - critical LCP optimization */}
              <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
                {productFacts.map((fact) => (
                  <div
                    key={fact.label}
                    className="min-w-0 rounded-xl bg-gray-800/95 px-3 py-3 text-center sm:px-4"
                  >
                    <div className="text-lg font-bold text-indigo-400 sm:text-2xl">{fact.value}</div>
                    <div className="mt-1 text-[11px] leading-4 text-gray-400 sm:text-sm sm:leading-5">
                      {fact.label}
                    </div>
                  </div>
                ))}
              </div>

              <p className="mx-auto mb-8 max-w-2xl text-sm leading-6 text-gray-400 [text-wrap:pretty] sm:text-base">
                {liveCoverageSummary}
              </p>

            </div>

            <div className="mx-auto mt-12 max-w-6xl">
              <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(160deg,rgba(15,23,42,0.86),rgba(30,41,59,0.78))] p-4 shadow-[0_28px_90px_rgba(2,6,23,0.28)] sm:p-6 lg:p-8">
                <div className="mb-6 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                  <div className="max-w-2xl text-left">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-emerald-200/78">
                      Penalty Run
                    </p>
                    <h2 className="mt-3 text-2xl font-bold tracking-[-0.03em] text-white sm:text-3xl lg:text-4xl">
                      Five shots. One score to beat.
                    </h2>
                    <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300 sm:text-base">
                      Aim, hold for power, and release inside the strike window. Your best run stays for this visit.
                    </p>
                  </div>

                </div>

                <DeferredPenaltyRun />
              </div>
            </div>
          </div>

          {/* Background elements */}
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 left-1/2 h-1 w-full max-w-7xl -translate-x-1/2 bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent"></div>
        </section>

        {/* Wrap the rest of the page content in LazyMotion with optimized animations */}
        <LazyMotion features={domAnimation}>
          {/* Sports Cards Section - optimized with content-visibility */}
          <section id="available-sports" className="py-16 md:py-24 container mx-auto px-4" style={{ contentVisibility: 'auto' }}>
            <div className="text-center mb-12">
              <span className="inline-block text-indigo-400 font-semibold mb-2">AVAILABLE NOW</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Choose Your Sport Before The Score Does</h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                Open the sport you care about, use spoiler-safe signals, and decide what to watch before score-first apps take over.
              </p>
            </div>
            
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              {sportCards.map((sport) => {
                const sportType = sport.name.toLowerCase() as SportType;
                const gameCount = getSportGameCount(sportType);
                const hasGames = gameCount > 0;
                const hasLoadedWithoutGames = !!gamesData?.games && gameCount === 0;

                return (
                  <motion.div
                    key={sport.name}
                    variants={itemVariants}
                  >
                    <Link
                      to={sport.path}
                      state={{ entrySource: 'home' }}
                      className={`group relative flex h-full min-h-[248px] flex-col overflow-hidden rounded-lg border border-white/10 bg-white/[0.045] p-5 shadow-lg shadow-black/20 transition-all duration-200 hover:-translate-y-1 hover:bg-white/[0.07] ${sport.hoverTone} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950`}
                    >
                      <div className={`absolute inset-x-0 top-0 h-1 ${sport.accent}`}></div>

                      <div className="flex items-start justify-between gap-3">
                        <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-md border ${sport.iconTone}`}>
                          {sport.icon}
                        </div>

                        {hasGames && (
                          <div className="inline-flex shrink-0 items-center rounded-md border border-emerald-300/25 bg-emerald-400/10 px-2.5 py-1 text-xs font-semibold text-emerald-100">
                            {gameCount} {gameCount === 1 ? 'game' : 'games'} ready
                          </div>
                        )}

                        {hasLoadedWithoutGames && (
                          <div className="inline-flex shrink-0 items-center rounded-md border border-white/15 bg-white/10 px-2.5 py-1 text-xs font-semibold text-white/80">
                            No games now
                          </div>
                        )}
                      </div>

                      <div className="mt-6 flex flex-1 flex-col">
                        <h3 className="text-2xl font-bold text-white">{sport.name}</h3>
                        <p className="mt-3 flex-1 text-sm leading-6 text-gray-300">{sport.description}</p>
                        <div className="mt-6 flex items-center text-sm font-semibold text-white">
                          <span>Choose games</span>
                          <ArrowRight className="ml-2 h-4 w-4 text-white/65 transition-all duration-150 group-hover:translate-x-1 group-hover:text-white" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </section>
          
          {/* Benefits Section */}
          <section className="py-16 md:py-24 container mx-auto px-4" style={{ contentVisibility: 'auto' }}>
            <div className="text-center mb-12">
              <span className="inline-block text-indigo-400 font-semibold mb-2">BENEFITS</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Delayed-Viewing Fans Use skore</h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                Built for the moment when you missed the game, still care about the suspense, and only have time for the right replay.
              </p>
            </div>
            
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              {benefits.map((benefit, index) => (
                <motion.div 
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-700/50 transition-all hover:border-indigo-500/30 hover:bg-gray-800/80"
                >
                  <div className="bg-indigo-500/10 w-16 h-16 rounded-xl flex items-center justify-center mb-6 text-indigo-400">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{benefit.title}</h3>
                  <p className="text-gray-400">{benefit.description}</p>
                  <ul className="mt-4 space-y-2">
                    <li className="flex items-center text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span>No account needed</span>
                    </li>
                    <li className="flex items-center text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span>Works on all devices</span>
                    </li>
                    <li className="flex items-center text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span>Completely free</span>
                    </li>
                  </ul>
                </motion.div>
              ))}
            </motion.div>
          </section>
          
          {/* How It Works Section - Simplified animations */}
          <section className="py-16 md:py-24 bg-gray-900/50" style={{ contentVisibility: 'auto' }}>
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <span className="inline-block text-indigo-400 font-semibold mb-2">SIMPLE PROCESS</span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works</h2>
                <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                  Three simple steps to make the replay choice before the rest of the internet ruins it
                </p>
              </div>
              
              <div className="max-w-5xl mx-auto">
                <div className="relative py-12">
                  {/* Connecting line */}
                  <motion.div 
                    className="pointer-events-none absolute left-0 right-0 top-1/2 h-1 bg-gradient-to-r from-orange-500 via-indigo-500 to-green-500"
                    initial={{ scaleX: 0, opacity: 0 }}
                    whileInView={{ scaleX: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
                    {processData.map((step, index) => (
                      <motion.div
                        key={index}
                        className="relative z-10"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1, duration: 0.3 }}
                      >
                        {/* Card */}
                        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-xl h-full">
                          {/* Step Number */}
                          <div className="font-bold text-4xl text-transparent bg-clip-text bg-gradient-to-br from-indigo-400 to-purple-300 mb-4">
                            {step.number}
                          </div>
                          
                          {/* Icon */}
                          <div className={`${step.color} w-12 h-12 rounded-full flex items-center justify-center mb-4`}>
                            {step.icon}
                          </div>
                          
                          {/* Content */}
                          <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                          <p className="text-gray-400">{step.description}</p>
                          
                          {/* Icon for 'watching games' */}
                          {index === 2 && (
                            <div className="mt-4 flex items-center justify-center">
                              <div className="flex items-center gap-2 text-green-400">
                                <CirclePlay className="w-5 h-5" />
                                <span className="text-sm font-medium">Enjoy spoiler-free!</span>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Circle Indicator */}
                        <div className={`absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 ${step.color} rounded-full border-2 border-gray-900 z-20 flex items-center justify-center`}>
                          <span className="text-white font-bold text-sm">{index + 1}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* Final step - watching games */}
                  <div className="mt-12 text-center">
                    <GameSelectionLink
                      destination={gameSelectionDestination}
                      testId="home-process-game-selection"
                      className="inline-flex min-h-12 cursor-pointer items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-3 font-medium text-white shadow-lg transition-all hover:-translate-y-0.5 hover:from-indigo-500 hover:to-purple-500 hover:shadow-indigo-600/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo-300"
                    >
                      <span>{gameSelectionDestination.label}</span>
                      <CirclePlay className="w-5 h-5" />
                    </GameSelectionLink>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16 md:py-24 container mx-auto px-4" style={{ contentVisibility: 'auto' }}>
            <div className="text-center mb-12">
              <span className="inline-block text-indigo-400 font-semibold mb-2">FAQ</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                Everything you need to know about watching sports without spoilers
              </p>
            </div>
            
            <div className="max-w-3xl mx-auto space-y-4">
              {faqItems.map((item, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05, duration: 0.2 }}
                  className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50"
                >
                  <h3 className="text-xl font-semibold text-white mb-3">{item.question}</h3>
                  <p className="text-gray-300">{item.answer}</p>
                </motion.div>
              ))}
              
              <div className="text-center mt-8">
                <Link 
                  to="/faq"
                  className="text-indigo-400 hover:text-indigo-300 font-medium inline-flex items-center"
                >
                  View all FAQs
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 md:py-24" style={{ contentVisibility: 'auto' }}>
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto bg-gradient-to-br from-indigo-900/70 to-purple-900/70 rounded-3xl p-8 md:p-12 text-center backdrop-blur-sm border border-white/10 shadow-2xl">
                <div className="inline-block mb-6 bg-white/10 px-4 py-2 rounded-full">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-300" />
                    <span className="text-indigo-300 text-sm font-medium">No account required. Scores stay hidden until you reveal them.</span>
                  </div>
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Ready to choose before score-first apps do?
                </h2>
                
                <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                  Start with the spoiler-safe decision step, then watch the replay on the platform you already use.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <GameSelectionLink
                    destination={gameSelectionDestination}
                    testId="home-final-game-selection"
                    className="group inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-full bg-white px-8 py-4 font-semibold text-indigo-900 shadow-lg transition-all duration-300 hover:scale-105 hover:bg-gray-100 hover:shadow-white/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
                  >
                    {gameSelectionDestination.label}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </GameSelectionLink>
                  
                  <Link 
                    to="/blog"
                    className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-full border border-white/30 bg-transparent px-8 py-4 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10"
                  >
                    Read our blog
                  </Link>
                </div>
                
                <p className="text-gray-400 mt-6 text-sm">
                  No sign-up required. 100% free to use.
                </p>
              </div>
            </div>
          </section>
        </LazyMotion>
      </main>
      
      <Footer />
    </div>
  );
};

export default Home;
